import React from 'react';
import { useLocation } from 'react-router-dom';
import { User } from '@auth0/auth0-react';
import { Tenancy } from '@hud/portal-dtos/webapp-common.dto';
import MCPBannerSidebarItem from '@hud/web-components/MCPBannerSidebarItem/MCPBannerSidebarItem';
import { useBooleanFeatureFlag } from '../../hooks/useFeatureFlags';
import useEnvironment from '../../hooks/useEnvironment';
import useSidebarQuery from '../../hooks/useSidebarQuery';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import useAccountInfo from '../../hooks/useAccountInfo';
import { Navigation } from '../Navigation/Navigation';
import AvatarMenu from '../AvatarMenu/AvatarMenu';
import { Container } from './SidebarV2.styles';
import Logo from '../../assets/logo.svg?react';

export interface SidebarV2Props {
  user: User | null;
  onLogout: () => void;
}

export function SidebarV2({ user, onLogout, ...rest }: SidebarV2Props) {
  const location = useLocation();
  const globalFilters = useGlobalQueryString();
  const enableLiveCode = useBooleanFeatureFlag('enable-live-code');
  const enableMCPBanner = useBooleanFeatureFlag('enable-mcp-sidebar-card');
  const enableOverviewPage = useBooleanFeatureFlag('enable-overview-page');
  const { data: accountInfoData } = useAccountInfo();
  const settingsDefaultRedirect =
    accountInfoData?.tenancy === Tenancy.enum.single
      ? '/settings/heads-up'
      : '/settings/members';

  return (
    <Container {...rest}>
      <div className="header">
        <Logo />
      </div>

      <div className="body">
        {enableOverviewPage && <HomeGroupMenu />}
        <ExploreGroupMenu />
        <HeadsUpGroupMenu />

        {enableLiveCode && (
          <Navigation.Group>
            <Navigation.Link to="/live-code">Live Code</Navigation.Link>
          </Navigation.Group>
        )}

        {enableMCPBanner ? <MCPBannerSidebarItem /> : null}
      </div>

      <div className="footer">
        <Navigation.Group>
          <Navigation.Link
            icon="designSystem.book"
            to="https://docs.hud.io/"
            target="_blank"
          >
            Documentation
          </Navigation.Link>
          <Navigation.Link
            icon="designSystem.cog"
            to={`${settingsDefaultRedirect}?${globalFilters}`}
            state={{ from: location }}
          >
            Settings
          </Navigation.Link>
          {user && <AvatarMenu user={user} onLogout={onLogout} caret={true} />}
        </Navigation.Group>
      </div>
    </Container>
  );
}

function getPreferredServicesUrlEncoded(userPreferredServices?: number[] | null) {
  if (!userPreferredServices) {
    return '';
  }
  if (userPreferredServices.length === 0) {
    return '';
  }
  return (
    '?services=' +
    encodeURIComponent(
      userPreferredServices.map((service) => encodeURIComponent(service)).join(','),
    )
  );
}

function HomeGroupMenu() {
  return (
    <Navigation.Group label="Home">
      <Navigation.Link to="/overview" icon="designSystem.compass">
        Overview
      </Navigation.Link>
    </Navigation.Group>
  );
}

function ExploreGroupMenu() {
  const { environment } = useEnvironment();
  const sidebarQuery = useSidebarQuery({ environmentName: environment?.name });
  const enableMessageQueues = useBooleanFeatureFlag('enable-kafka-topics'); // legacy name
  const enablePRComparisonsPage = useBooleanFeatureFlag('enable-pr-comparisons-page');
  const { userPreferredServices } = useUserPreferences();
  const preferredServicesAppendix = getPreferredServicesUrlEncoded(userPreferredServices);

  return (
    <Navigation.Group label="Explore">
      <Navigation.Link
        to="/explore/services"
        icon="designSystem.service"
        aside={sidebarQuery.data?.totalServices || ''}
      >
        Services
      </Navigation.Link>
      <Navigation.Link
        to={`/explore/endpoints${preferredServicesAppendix}`}
        icon="designSystem.endpoint"
        aside={sidebarQuery.data?.totalEndpoints || ''}
      >
        Endpoints
      </Navigation.Link>
      {enableMessageQueues && (
        <Navigation.Link
          to={`/explore/messageQueues${preferredServicesAppendix}`}
          icon="designSystem.queue"
          aside={sidebarQuery.data?.totalMessageQueues || ''}
        >
          Queues
        </Navigation.Link>
      )}
      <Navigation.Link
        to={`/explore/functions${preferredServicesAppendix}`}
        icon="designSystem.function"
        aside={sidebarQuery.data?.totalFunctions || ''}
      >
        Functions
      </Navigation.Link>
      {enablePRComparisonsPage && (
        <Navigation.Link to="/explore/prs" icon="designSystem.comparison">
          PR Comparisons
        </Navigation.Link>
      )}
    </Navigation.Group>
  );
}

function HeadsUpGroupMenu() {
  const { userPreferredServices } = useUserPreferences();
  const preferredServicesAppendix = getPreferredServicesUrlEncoded(userPreferredServices);

  return (
    <Navigation.Group label="Heads Up">
      <Navigation.Link
        to={`/heads-up/issues${preferredServicesAppendix}`}
        icon="designSystem.flame"
      >
        Issues
      </Navigation.Link>
      <Navigation.Link to="/heads-up/rules" icon="designSystem.andGate">
        Rules
      </Navigation.Link>
    </Navigation.Group>
  );
}
